import Axios from "axios";
import {appConfig} from "./app-config";
import {isWorkspaceScopedMutation} from "./api-request-guards";
import {getUser, setUser} from "./storage";

export {isWorkspaceScopedMutation} from "./api-request-guards";

let errorHandler = null;
let unauthorizedHandler = null;
let workspaceMutationGuard = null;
let refreshPromise = null;


const axiosInstance = Axios.create({
    baseURL: appConfig.apiUrl,
    // timeout: 20000,
    headers: {Accept: "application/json"},
});

export const setHeaderToken = (token) => {

    if (token){
        axiosInstance.defaults.headers = {...axiosInstance.defaults.headers, Authorization:token};
    }
    else {
        delete axiosInstance.defaults.headers['Authorization'];
    }
};

export const getAuthToken = (user) => user?.jwt || user?.token;

export const refreshAuthSession = async () => {
    const storedUser = getUser();
    if (!storedUser?.refreshToken) {
        throw new Error("user-session.error.refresh-token-missing");
    }

    if (!refreshPromise) {
        refreshPromise = Axios.post(`${appConfig.apiUrl}/auth/refresh`, {
            refreshToken: storedUser.refreshToken,
        }, {headers: {Accept: "application/json"}}).then(({data}) => {
            const nextUser = {...getUser(), ...data};
            setUser(nextUser);
            setHeaderToken(`Bearer ${nextUser.jwt}`);
            window.dispatchEvent(new CustomEvent("platform:auth-refreshed", {detail: nextUser}));
            return nextUser;
        }).finally(() => {
            refreshPromise = null;
        });
    }

    return refreshPromise;
};

export const normalizeApiError = (error) => {
    const data = error?.response?.data;
    const backendError = data?.error;
    const details = backendError?.details || {};
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const message =
        data?.error?.message ||
        data?.error ||
        data?.message ||
        error?.message ||
        "api-error";

    return {
        status,
        data,
        url,
        message,
        code: details?.code || backendError?.code || error?.code,
        details,
        retryable: Boolean(details?.retryable),
        runId: details?.runId || null,
        stepId: details?.stepId || null,
        isNetworkError: !error?.response && (error?.code === "ERR_NETWORK" || error?.message === "Network Error"),
        original: error,
    };
};

export const setApiErrorHandler = (handler) => {
    errorHandler = handler;
};

export const setApiUnauthorizedHandler = (handler) => {
    unauthorizedHandler = handler;
};

export const setWorkspaceMutationGuard = (handler) => {
    workspaceMutationGuard = typeof handler === "function" ? handler : null;
};

axiosInstance.interceptors.request.use((config) => {
    if (!workspaceMutationGuard || !isWorkspaceScopedMutation(config)) return config;

    const result = workspaceMutationGuard(config);
    const allowed = typeof result === "boolean" ? result : result?.allowed !== false;
    if (allowed) return config;

    const error = new Error(result?.message || "no-permission");
    error.code = "WORKSPACE_READ_ONLY";
    error.config = config;
    return Promise.reject(error);
});

axiosInstance.interceptors.response.use(
    response => response,
    async error => {
        const normalizedError = normalizeApiError(error);
        error.normalizedError = normalizedError;
        error.message = normalizedError.message;

        const request = error?.config || {};
        const storedUser = getUser();
        const canRefresh = normalizedError.status === 401
            && !request._sessionRefreshAttempted
            && !String(request.url || "").includes("/auth/refresh")
            && storedUser?.refreshToken;
        if (canRefresh) {
            request._sessionRefreshAttempted = true;
            try {
                const refreshedUser = await refreshAuthSession();
                request.headers = {...request.headers, Authorization: `Bearer ${refreshedUser.jwt}`};
                return axiosInstance(request);
            } catch (refreshError) {
                const refreshFailure = normalizeApiError(refreshError);
                if (unauthorizedHandler) unauthorizedHandler({...refreshFailure, message: normalizedError.message, code: normalizedError.code});
                return Promise.reject(error);
            }
        }

        if (normalizedError.status === 401 && unauthorizedHandler) {
            unauthorizedHandler(normalizedError);
        }
        else if (errorHandler && !error?.config?.suppressGlobalErrorHandler) {
            errorHandler(normalizedError);
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
